import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Radar } from 'react-chartjs-2';
import { api } from '../api';
import { estimate1RM } from '../lib/calc';
import { computeStreak, buildHeatMap } from '../lib/activity';
import { Heatmap } from '../components/ui';

ChartJS.register(
  CategoryScale, LinearScale, RadialLinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler
);

const CHART_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: '#94a3b8', font: { size: 12 } } },
    tooltip: { backgroundColor: '#18181b', borderColor: '#27272a', borderWidth: 1 },
  },
  scales: {
    x: { ticks: { color: '#94a3b8', maxTicksLimit: 8 }, grid: { color: '#27272a' } },
    y: { ticks: { color: '#94a3b8' }, grid: { color: '#27272a' } },
  },
};

function ExerciseChart({ exerciseId }) {
  const [metric, setMetric] = useState('max_weight');
  const [period, setPeriod] = useState(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['stats', 'exercise', exerciseId],
    queryFn: () => api.getExerciseStats(exerciseId),
    enabled: !!exerciseId,
  });

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement...</div>;
  if (data.length === 0) return <div className="empty-state"><div className="icon">📉</div><p>Pas encore de données</p></div>;

  const cutoff = period ? new Date(Date.now() - period * 86400000) : null;
  const filteredData = cutoff ? data.filter(d => new Date(d.date + 'T12:00:00') >= cutoff) : data;

  const labels = filteredData.map((d) => new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }));

  const datasets = {
    max_weight: {
      label: 'Poids max (kg)',
      data: filteredData.map((d) => d.max_weight),
      borderColor: '#10b981',
      backgroundColor: 'rgba(99,102,241,0.1)',
      fill: true,
      tension: 0.3,
    },
    e1rm: {
      label: '1RM estimé (kg)',
      data: filteredData.map((d) => {
        const values = d.sets.map((x) => estimate1RM(x.weight, x.reps)).filter((v) => v != null);
        return values.length ? Math.round(Math.max(...values) * 10) / 10 : null;
      }),
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245,158,11,0.1)',
      fill: true,
      tension: 0.3,
      spanGaps: true,
    },
    total_volume: {
      label: 'Volume total (kg)',
      data: filteredData.map((d) => d.total_volume),
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34,197,94,0.1)',
      fill: true,
      tension: 0.3,
    },
  };

  // Per-set details for last session
  const last = filteredData[filteredData.length - 1];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[30, 90, 180, null].map((p) => (
          <button key={p} className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriod(p)}>
            {p === null ? 'Tout' : p === 30 ? '30j' : p === 90 ? '90j' : '6m'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['max_weight', 'e1rm', 'total_volume'].map((m) => (
          <button
            key={m}
            className={`btn btn-sm ${metric === m ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setMetric(m)}
          >
            {m === 'max_weight' ? '⚖️ Poids max' : m === 'e1rm' ? '🎯 1RM estimé' : '📦 Volume'}
          </button>
        ))}
      </div>
      <div className="chart-container">
        <Line data={{ labels, datasets: [datasets[metric]] }} options={CHART_OPTS} />
      </div>
      {last && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', marginBottom: 10 }}>
            Dernière séance — {new Date(last.date).toLocaleDateString('fr-FR')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {last.sets.map((s) => (
              <div
                key={s.set_number}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 14,
                }}
              >
                <span style={{ color: 'var(--text2)', marginRight: 6 }}>S{s.set_number}</span>
                <strong>{s.weight ?? '?'} kg</strong>
                <span style={{ color: 'var(--text2)', margin: '0 4px' }}>×</span>
                <strong>{s.reps ?? '?'} reps</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewChart() {
  const [period, setPeriod] = useState(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: api.getOverview,
  });

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement...</div>;
  if (data.length === 0) return <div className="empty-state"><div className="icon">📉</div><p>Pas encore de données</p></div>;

  const cutoff = period ? new Date(Date.now() - period * 86400000) : null;
  const filteredData = cutoff ? data.filter(d => new Date(d.date + 'T12:00:00') >= cutoff) : data;

  const labels = filteredData.map((d) => new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Volume total (kg)',
        data: filteredData.map((d) => d.total_volume),
        backgroundColor: 'rgba(99,102,241,0.7)',
        borderRadius: 6,
      },
    ],
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[30, 90, 180, null].map((p) => (
          <button key={p} className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPeriod(p)}>
            {p === null ? 'Tout' : p === 30 ? '30j' : p === 90 ? '90j' : '6m'}
          </button>
        ))}
      </div>
      <div className="chart-container">
        <Bar data={chartData} options={CHART_OPTS} />
      </div>
    </div>
  );
}

function WeeklyMuscles() {
  const [weeks, setWeeks] = useState(8);
  const { data = [], isLoading } = useQuery({
    queryKey: ['stats', 'muscle-weekly', weeks],
    queryFn: () => api.getMuscleWeekly(weeks),
  });
  const [metric, setMetric] = useState('sets');

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement...</div>;
  if (!data.length) return <div className="empty-state"><div className="icon">🦵</div><p>Pas encore de données</p></div>;

  const weekKeys = [...new Set(data.map((d) => d.week))].sort();
  const muscles = [...new Set(data.map((d) => d.muscle))];
  const value = (week, muscle) => data.find((d) => d.week === week && d.muscle === muscle)?.[metric] || 0;
  const palette = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7', '#14b8a6', '#ec4899', '#64748b', '#84cc16', '#f97316'];
  const lastWeek = weekKeys[weekKeys.length - 1];
  const radarMuscles = muscles.filter((m) => value(lastWeek, m) > 0 || (weekKeys.length > 1 && value(weekKeys[weekKeys.length - 2], m) > 0));
  const label = (w) => new Date(`${w}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

  const stackedOpts = {
    ...CHART_OPTS,
    scales: {
      x: { ...CHART_OPTS.scales.x, stacked: true },
      y: { ...CHART_OPTS.scales.y, stacked: true },
    },
  };

  return (
    <div>
      <div className="chip-row" style={{ marginBottom: 12 }}>
        {[['sets', 'Séries'], ['volume', 'Volume (kg)']].map(([key, text]) => (
          <button key={key} className={`btn btn-sm ${metric === key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMetric(key)}>{text}</button>
        ))}
        <span style={{ flex: 1 }} />
        {[4, 8, 12].map((w) => (
          <button key={w} className={`btn btn-sm ${weeks === w ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setWeeks(w)}>{w} sem.</button>
        ))}
      </div>
      <div className="chart-container" style={{ height: 320 }}>
        <Bar
          data={{
            labels: weekKeys.map(label),
            datasets: muscles.map((m, i) => ({ label: m, data: weekKeys.map((w) => value(w, m)), backgroundColor: palette[i % palette.length] })),
          }}
          options={stackedOpts}
        />
      </div>
      {radarMuscles.length >= 3 && (
        <div style={{ marginTop: 24 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>Équilibre : semaine du {label(lastWeek)} vs précédente</div>
          <div className="chart-container" style={{ height: 320 }}>
            <Radar
              data={{
                labels: radarMuscles,
                datasets: [
                  ...(weekKeys.length > 1 ? [{
                    label: 'Semaine précédente',
                    data: radarMuscles.map((m) => value(weekKeys[weekKeys.length - 2], m)),
                    borderColor: '#64748b', backgroundColor: 'rgba(100,116,139,0.15)',
                  }] : []),
                  { label: 'Cette semaine', data: radarMuscles.map((m) => value(lastWeek, m)), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.25)' },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: CHART_OPTS.plugins,
                scales: { r: { ticks: { color: '#94a3b8', backdropColor: 'transparent' }, grid: { color: '#27272a' }, angleLines: { color: '#27272a' }, pointLabels: { color: '#94a3b8' } } },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityTab() {
  const { data: sessions = [] } = useQuery({ queryKey: ['sessions'], queryFn: api.getSessions });
  const done = sessions.filter((s) => s.total_sets > 0);
  const streak = computeStreak(done);
  const grid = buildHeatMap(done, 26);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const last30 = done.filter((s) => s.date >= monthAgo).length;
  return (
    <div className="card">
      <div className="body-summary">
        <div><div className="stat-value">{streak}</div><div className="stat-label">Semaines consécutives</div></div>
        <div><div className="stat-value">{last30}</div><div className="stat-label">Séances sur 30 jours</div></div>
        <div><div className="stat-value">{done.length}</div><div className="stat-label">Séances au total</div></div>
      </div>
      <div style={{ overflowX: 'auto' }}><Heatmap grid={grid} size={16} /></div>
    </div>
  );
}

export default function Stats() {
  const [searchParams] = useSearchParams();
  const defaultExId = searchParams.get('exercise') ? parseInt(searchParams.get('exercise')) : null;

  const [selectedExercise, setSelectedExercise] = useState(defaultExId);
  const [tab, setTab] = useState('exercise');

  const { data: exercises = [] } = useQuery({
    queryKey: ['exercises'],
    queryFn: api.getExercises,
  });

  const { data: bests = [] } = useQuery({
    queryKey: ['bests'],
    queryFn: api.getBests,
  });

  const byGroup = {};
  for (const ex of exercises) {
    if (!byGroup[ex.day]) byGroup[ex.day] = [];
    byGroup[ex.day].push(ex);
  }

  return (
    <div>
      <h1 className="page-title">Statistiques</h1>
      <p className="page-subtitle">Suis ta progression et tes performances</p>

      <div className="tabs">
        <button className={`tab-btn ${tab === 'exercise' ? 'active' : ''}`} onClick={() => setTab('exercise')}>
          📈 Progression par exercice
        </button>
        <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          📊 Volume global
        </button>
        <button className={`tab-btn ${tab === 'bests' ? 'active' : ''}`} onClick={() => setTab('bests')}>
          🏆 Meilleures perfs
        </button>
        <button className={`tab-btn ${tab === 'muscles' ? 'active' : ''}`} onClick={() => setTab('muscles')}>
          🦵 Volume hebdo
        </button>
        <button className={`tab-btn ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>
          🗓 Activité
        </button>
      </div>

      {tab === 'exercise' && (
        <div className="grid grid-2" style={{ alignItems: 'start' }}>
          {/* Exercise selector */}
          <div className="card" style={{ maxHeight: 520, overflowY: 'auto' }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Choisir un exercice</div>
            {Object.keys(byGroup).map((group) => (
              <div key={group} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {group}
                </div>
                {(byGroup[group] || []).filter((ex) => !ex.alternative_to).map((ex) => {
                  const alternatives = (byGroup[group] || []).filter((alternative) => alternative.alternative_to === ex.id);
                  return (
                    <div key={ex.id}>
                      <button
                        className={`nav-link ${selectedExercise === ex.id ? 'active' : ''}`}
                        style={{ marginBottom: 2, textAlign: 'left', width: '100%' }}
                        onClick={() => setSelectedExercise(ex.id)}
                      >
                        {ex.name}
                      </button>
                      {alternatives.map((alternative, index) => (
                        <button
                          key={alternative.id}
                          className={`nav-link ${selectedExercise === alternative.id ? 'active' : ''}`}
                          style={{ marginBottom: 2, textAlign: 'left', width: '100%', paddingLeft: 20, fontSize: 13, opacity: 0.8 }}
                          onClick={() => setSelectedExercise(alternative.id)}
                        >
                          ↳ Alternative {index + 1} : {alternative.name}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="card">
            {selectedExercise ? (
              <>
                <div className="card-title" style={{ marginBottom: 16 }}>
                  {exercises.find((e) => e.id === selectedExercise)?.name}
                  {exercises.find((e) => e.id === selectedExercise)?.alternative_to && (
                    <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 400, marginLeft: 8 }}>
                      (données fusionnées avec l'exercice principal)
                    </span>
                  )}
                </div>
                <ExerciseChart exerciseId={selectedExercise} />
              </>
            ) : (
              <div className="empty-state">
                <div className="icon">👈</div>
                <p>Sélectionne un exercice</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'muscles' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Volume hebdomadaire par groupe musculaire</div>
          <WeeklyMuscles />
        </div>
      )}

      {tab === 'activity' && <ActivityTab />}

      {tab === 'overview' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 20 }}>Volume par séance</div>
          <OverviewChart />
        </div>
      )}

      {tab === 'bests' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 20 }}>Meilleures performances</div>
          {bests.length === 0 ? (
            <div className="empty-state">
              <div className="icon">🏆</div>
              <p>Pas encore de données enregistrées</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Exercice</th>
                    <th>Jour</th>
                    <th>Poids max</th>
                    <th>Reps max</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {bests.map((b) => (
                    <tr
                      key={b.exercise_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => { setSelectedExercise(b.exercise_id); setTab('exercise'); }}
                    >
                      <td style={{ fontWeight: 600 }}>{b.exercise_name}</td>
                      <td><span style={{ color: 'var(--text2)' }}>{b.day}</span></td>
                      <td><strong style={{ color: 'var(--accent2)' }}>{b.max_weight ?? '—'} kg</strong></td>
                      <td>{b.max_reps ?? '—'}</td>
                      <td style={{ color: 'var(--text2)', fontSize: 13 }}>
                        {b.best_date ? new Date(b.best_date).toLocaleDateString('fr-FR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
